import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Listing } from '../types';
import { compareListings } from '../services/api';
import StarRating from '../components/StarRating';

const ComparePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const ids = (searchParams.get('ids') || '')
    .split(',')
    .map(s => Number(s.trim()))
    .filter(n => n > 0);

  useEffect(() => {
    const load = async () => {
      if (ids.length < 2) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await compareListings(ids);
        setListings(Array.isArray(data) ? data : []);
      } catch {
        setError('Failed to load listings for comparison.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const removeListing = (listingId: number) => {
    const remaining = ids.filter(id => id !== listingId);
    setSearchParams(remaining.length > 0 ? { ids: remaining.join(',') } : {});
    setListings(prev => prev.filter(l => l.listingId !== listingId));
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px', textAlign: 'center', color: '#666' }}>
        Loading comparison...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2>Error</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <Link to="/search" style={{ color: '#1a237e', fontWeight: 600 }}>← Browse Listings</Link>
      </div>
    );
  }

  if (ids.length < 2 || listings.length < 2) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚖️</div>
        <h2>Compare Listings</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          Add at least 2 listings to compare them side by side.
        </p>
        <Link to="/search" style={{
          display: 'inline-block', padding: '10px 24px', background: '#1a237e', color: '#fff',
          borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '14px',
        }}>Browse Listings</Link>
      </div>
    );
  }

  const rows: { label: string; render: (l: Listing) => React.ReactNode }[] = [
    { label: 'Name', render: l => <strong>{l.name}</strong> },
    { label: 'City, State', render: l => l.address ? `${l.address.city}, ${l.address.region}` : '—' },
    { label: 'Listing Type', render: l => l.listingType?.join(', ') || '—' },
    { label: 'Category', render: l => l.category?.join(', ') || '—' },
    {
      label: 'Program Types', render: l => l.programTypes?.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {l.programTypes.map((t, i) => <Tag key={i} text={t} bg="#e3f2fd" color="#1565c0" />)}
        </div>
      ) : '—',
    },
    {
      label: 'Treatment Types', render: l => l.treatmentTypes?.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {l.treatmentTypes.map((t, i) => <Tag key={i} text={t} bg="#f3e5f5" color="#7b1fa2" />)}
        </div>
      ) : '—',
    },
    {
      label: 'Insurance Accepted', render: l => l.insuranceAccepted?.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {l.insuranceAccepted.map((t, i) => <Tag key={i} text={t} bg="#e8f5e9" color="#2e7d32" />)}
        </div>
      ) : '—',
    },
    {
      label: 'Financing Available', render: l => (
        <span style={{
          padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
          background: l.financingAvailable ? '#e8f5e9' : '#f5f5f5',
          color: l.financingAvailable ? '#2e7d32' : '#999',
        }}>{l.financingAvailable ? 'Yes' : 'No'}</span>
      ),
    },
    { label: 'Environmental Setting', render: l => l.environmentalSetting?.join(', ') || '—' },
    {
      label: 'Overall Rating', render: l => (
        <StarRating rating={l.ratings?.overall?.value || 0} size="sm" />
      ),
    },
    { label: 'Review Count', render: l => l.ratings?.publishedReviewsCount || 0 },
    {
      label: 'Pricing Available', render: l => {
        const hasPricing = l.showPricingToCustomer || (l.pricing && l.pricing.length > 0);
        return (
          <span style={{
            padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
            background: hasPricing ? '#e8f5e9' : '#f5f5f5',
            color: hasPricing ? '#2e7d32' : '#999',
          }}>{hasPricing ? 'Yes' : 'No'}</span>
        );
      },
    },
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px' }}>
      <Link to="/search" style={{ color: '#1a237e', textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>
        ← Back to Search
      </Link>
      <h1 style={{ fontSize: '28px', margin: '16px 0 8px' }}>Compare Listings</h1>
      <p style={{ color: '#666', margin: '0 0 24px', fontSize: '14px' }}>
        Comparing {listings.length} listings side by side
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: `${listings.length * 220 + 160}px` }}>
          {/* Sticky Header */}
          <thead>
            <tr>
              <th style={{
                ...stickyHeaderStyle, width: '160px', minWidth: '160px',
                background: '#1a237e', color: '#fff', borderRadius: '12px 0 0 0',
              }}>
                Feature
              </th>
              {listings.map((l, idx) => (
                <th key={l.listingId} style={{
                  ...stickyHeaderStyle, background: '#1a237e', color: '#fff',
                  borderRadius: idx === listings.length - 1 ? '0 12px 0 0' : undefined,
                }}>
                  <div style={{ marginBottom: '8px', fontSize: '15px' }}>{l.name}</div>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    <Link to={`/listing/${l.listingId}`} style={{
                      padding: '4px 10px', background: 'rgba(255,255,255,0.2)', color: '#fff',
                      borderRadius: '4px', fontSize: '11px', textDecoration: 'none', fontWeight: 600,
                    }}>View Details</Link>
                    <button onClick={() => removeListing(l.listingId)} style={{
                      padding: '4px 10px', background: 'rgba(255,255,255,0.15)', color: '#ffcdd2',
                      borderRadius: '4px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                    }}>✕ Remove</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={row.label}>
                <td style={{
                  ...cellStyle,
                  fontWeight: 600, color: '#333', fontSize: '13px',
                  background: rowIdx % 2 === 0 ? '#f8f9fa' : '#fff',
                }}>{row.label}</td>
                {listings.map(l => (
                  <td key={l.listingId} style={{
                    ...cellStyle, fontSize: '14px', color: '#444',
                    background: rowIdx % 2 === 0 ? '#f8f9fa' : '#fff',
                  }}>
                    {row.render(l)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Tag: React.FC<{ text: string; bg: string; color: string }> = ({ text, bg, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
    fontSize: '11px', background: bg, color,
  }}>{text}</span>
);

const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  padding: '16px 12px', textAlign: 'center', fontSize: '14px', fontWeight: 700,
};

const cellStyle: React.CSSProperties = {
  padding: '14px 12px', borderBottom: '1px solid #e0e0e0', verticalAlign: 'top',
};

export default ComparePage;
